// Dinero del módulo de control financiero.
//
// 🔴 POR QUÉ NO SE USA `formatMXN` DE lib/api.js: ese formateador lleva
//    `maximumFractionDigits: 0`, así que **redondea los centavos** — $1,234.50 se pinta
//    "$1,235" — y además pasa el valor por `Number`, que es justo el float que este módulo
//    prohíbe. Es DT.236; NO se toca aquí porque cambiarlo mueve 6 pantallas que hoy no
//    tienen centavos, y eso es una entrega con su propia regresión.
//
// 🔴 LA REGLA DE ESTE ARCHIVO: los importes llegan del backend como CADENA ("750.00",
//    escala exacta, verificado en el JSON real de producción) y **no se convierten a
//    número para operar**. Si hay que sumar, se suma sobre ENTEROS DE CENTAVOS. Un
//    `parseFloat("0.1") + parseFloat("0.2")` da 0.30000000000000004, y en dinero eso es
//    un centavo que aparece de la nada.
//
// El único punto donde un importe se convierte a número es la ÚLTIMA línea antes de
// pintarlo, y ahí ya no se opera con él: sólo se le da formato.

/** "750.5" | "750" | "750.50" -> 75050 (centavos, entero). Devuelve null si no es válido. */
export function aCentavos(valor) {
  if (valor === null || valor === undefined) return null;
  const s = String(valor).trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  const negativo = s.startsWith("-");
  const [ent, dec = ""] = (negativo ? s.slice(1) : s).split(".");
  const centavos = Number(ent) * 100 + Number((dec + "00").slice(0, 2));
  return negativo ? -centavos : centavos;
}

/** 75050 -> "750.50". La forma canónica que espera el backend. */
export function deCentavos(centavos) {
  if (centavos === null || centavos === undefined || Number.isNaN(centavos)) return "0.00";
  const neg = centavos < 0;
  const abs = Math.abs(Math.round(centavos));
  return `${neg ? "-" : ""}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/**
 * Formatea un importe para mostrarlo. Acepta la CADENA que manda el backend.
 * Siempre con dos decimales: en dinero, "$1,234.5" está mal escrito y "$1,235" es mentira.
 */
export function formatoDinero(valor) {
  const c = aCentavos(valor);
  if (c === null) return "—";
  // Se pasa por Number sólo AQUI, para dar formato, y ya no se opera con el resultado.
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(c / 100);
}

/**
 * Importe de una línea, en centavos: cantidad × precio − descuento.
 *
 * ⚠️ Esto NO es "calcular el total en el frontend". El total real lo calcula el motor
 * (`cobro_lineas.total` es GENERATED) y es el que se muestra una vez guardado. Esto es
 * sólo la vista previa mientras el usuario escribe, para que vea lo que va a cobrar antes
 * de mandarlo. La cantidad admite 3 decimales, así que se multiplica en milésimas y se
 * redondea a centavos igual que hace `ROUND(cantidad * precio_unitario, 2)` en Postgres.
 */
export function previsualizarLinea({ cantidad, precio_unitario, descuento }) {
  const milesimas = aMilesimas(cantidad);
  const precioC = aCentavos(precio_unitario);
  const descC = aCentavos(descuento) ?? 0;
  if (milesimas === null || precioC === null) return null;
  const bruto = Math.round((milesimas * precioC) / 1000);
  return bruto - descC;
}

/** "1.5" -> 1500 (milésimas). Para la cantidad, que admite 3 decimales. */
export function aMilesimas(valor) {
  if (valor === null || valor === undefined) return null;
  const s = String(valor).trim();
  if (!/^\d+(\.\d{1,3})?$/.test(s)) return null;
  const [ent, dec = ""] = s.split(".");
  return Number(ent) * 1000 + Number((dec + "000").slice(0, 3));
}
